import React, { useState, useRef } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { 
  Wand2, Download, Printer, Image, Palette, 
  Sparkles, FileText, Eye, Plus, Upload,
  Loader2, Coffee, Pizza, ChefHat, RefreshCw, Check
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Templates disponibles - 3 principaux + personnalisation
const TEMPLATES = [
  {
    id: "dark_elegant",
    name: "Moderne Sombre",
    description: "Fond noir avec accents dorés, style premium",
    preview: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
    accent: "#d4af37",
    background: "#1a1a1a",
    textColor: "#ffffff"
  },
  {
    id: "classic_elegant",
    name: "Classique Élégant",
    description: "Tons crème et bordeaux, style bistrot parisien",
    preview: "linear-gradient(135deg, #faf8f5 0%, #f5f0e8 100%)",
    accent: "#8b1a1a",
    background: "#faf8f5",
    textColor: "#2d2d2d"
  },
  {
    id: "minimalist",
    name: "Minimaliste",
    description: "Fond blanc épuré, typographie moderne",
    preview: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    accent: "#2d2d2d",
    background: "#ffffff",
    textColor: "#1a1a1a"
  }
];

const MenuGeneratorModal = ({ 
  isOpen, 
  onClose, 
  foodTruck, 
  planningId, 
  selectedDay,
  token 
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState("dark_elegant");
  const [aiProvider, setAiProvider] = useState("claude");
  const [generateIcons, setGenerateIcons] = useState(false);
  const [customTagline, setCustomTagline] = useState("");
  const [customHeaderImage, setCustomHeaderImage] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [generatedHtml, setGeneratedHtml] = useState(null);
  const [enhancedItems, setEnhancedItems] = useState([]);
  const [activeTab, setActiveTab] = useState("template");
  const [iconGenerating, setIconGenerating] = useState({});
  
  // Custom colors state
  const [useCustomColors, setUseCustomColors] = useState(false);
  const [customBackground, setCustomBackground] = useState("#1a1a1a");
  const [customTextColor, setCustomTextColor] = useState("#ffffff");
  const [customAccentColor, setCustomAccentColor] = useState("#d4af37");
  
  const previewRef = useRef(null);

  // Get menu items from food truck
  const getMenuItems = () => {
    if (!foodTruck?.menu_par_jour) return [];
    
    // Try to get menu for selected day
    const dayMap = {
      "Jeudi": ["Jeudi"],
      "Vendredi": ["Vendredi"],
      "Samedi": ["Samedi", "Samedi Midi", "Samedi Soir"],
      "Samedi Midi": ["Samedi Midi", "Samedi"],
      "Samedi Soir": ["Samedi Soir", "Samedi"]
    };
    
    const dayKeys = dayMap[selectedDay] || Object.keys(foodTruck.menu_par_jour);
    
    for (const key of dayKeys) {
      if (foodTruck.menu_par_jour[key]?.length > 0) {
        return foodTruck.menu_par_jour[key].map(item => ({
          nom: item.nom || "",
          prix: item.prix || 0,
          description: item.description || "",
          categorie: item.categorie || "",
          tags: item.tags || [],
          image_url: item.image_url || null
        }));
      }
    }
    
    // Fallback to general menu
    if (foodTruck.menu?.length > 0) {
      return foodTruck.menu.map(item => ({
        nom: item.nom || "",
        prix: item.prix || 0,
        description: item.description || "",
        categorie: item.categorie || "",
        tags: item.tags || [],
        image_url: item.image_url || null
      }));
    }
    
    return [];
  };

  const [menuItems, setMenuItems] = useState(getMenuItems());

  // Reset items when food truck changes
  React.useEffect(() => {
    setMenuItems(getMenuItems());
    setEnhancedItems([]);
    setGeneratedHtml(null);
    // eslint-disable-next-line
  }, [foodTruck, selectedDay]);
  
  // Update custom colors when template changes
  React.useEffect(() => {
    const template = TEMPLATES.find(t => t.id === selectedTemplate);
    if (template && !useCustomColors) {
      setCustomBackground(template.background);
      setCustomTextColor(template.textColor);
      setCustomAccentColor(template.accent);
    }
  }, [selectedTemplate, useCustomColors]);

  // Enhance menu with AI
  const handleEnhanceMenu = async () => {
    if (menuItems.length === 0) {
      toast.error("Aucun plat à améliorer");
      return;
    }

    setIsEnhancing(true);
    try {
      const response = await fetch(`${API_URL}/api/menu-generator/enhance-menu`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          food_truck_name: foodTruck?.nom || "Food Truck",
          food_truck_specialite: foodTruck?.specialite || "",
          jour: selectedDay || "Menu",
          items: menuItems,
          ai_provider: aiProvider
        })
      });

      const data = await response.json();
      
      if (response.ok && data.enhanced_data) {
        // Merge enhanced data with existing items
        const enhanced = menuItems.map((item) => {
          const enhancedItem = data.enhanced_data.enhanced_items?.find(
            e => e.nom?.toLowerCase() === item.nom?.toLowerCase()
          ) || {};
          return {
            ...item,
            description: enhancedItem.description || item.description,
            tags: enhancedItem.tags || item.tags,
            categorie: enhancedItem.categorie || item.categorie
          };
        });
        
        setEnhancedItems(enhanced);
        setMenuItems(enhanced);
        
        if (data.enhanced_data.tagline) {
          setCustomTagline(data.enhanced_data.tagline);
        }
        
        toast.success(`Menu amélioré avec ${aiProvider === 'claude' ? 'Claude' : 'GPT-4'} !`);
      } else {
        throw new Error(data.detail || "Erreur lors de l'amélioration");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsEnhancing(false);
    }
  };

  // Generate icon for an item
  const handleGenerateIcon = async (itemIndex) => {
    const item = menuItems[itemIndex];
    setIconGenerating(prev => ({ ...prev, [itemIndex]: true }));
    
    try {
      const response = await fetch(`${API_URL}/api/menu-generator/generate-icon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          item_name: item.nom,
          item_description: item.description || foodTruck?.specialite || "",
          style: "realistic"
        })
      });

      const data = await response.json();
      
      if (response.ok && data.image_base64) {
        const imageUrl = `data:image/png;base64,${data.image_base64}`;
        const updatedItems = [...menuItems];
        updatedItems[itemIndex] = { ...updatedItems[itemIndex], image_url: imageUrl };
        setMenuItems(updatedItems);
        toast.success(`Image générée pour ${item.nom}`);
      } else {
        throw new Error(data.detail || "Erreur génération image");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIconGenerating(prev => ({ ...prev, [itemIndex]: false }));
    }
  };

  // Generate menu HTML
  const handleGenerateMenu = async () => {
    if (menuItems.length === 0) {
      toast.error("Aucun plat dans le menu");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/menu-generator/generate-menu-html`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          food_truck_name: foodTruck?.nom || "Food Truck",
          food_truck_specialite: foodTruck?.specialite || "",
          zone: foodTruck?.zone || "",
          jour: selectedDay || "Menu",
          items: menuItems,
          template_id: selectedTemplate,
          ai_provider: aiProvider,
          generate_icons: generateIcons,
          custom_header_image: customHeaderImage,
          custom_tagline: customTagline,
          // Send custom colors if enabled
          custom_background: useCustomColors ? customBackground : null,
          custom_text_color: useCustomColors ? customTextColor : null,
          custom_accent_color: useCustomColors ? customAccentColor : null
        })
      });

      const data = await response.json();
      
      if (response.ok && data.html) {
        setGeneratedHtml(data.html);
        setActiveTab("preview");
        toast.success("Menu généré avec succès !");
      } else {
        throw new Error(data.detail || "Erreur lors de la génération");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  // Export as PDF
  const handleExportPdf = () => {
    if (!generatedHtml) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatedHtml);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Handle image upload
  const handleImageUpload = (e, itemIndex = null) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageUrl = event.target.result;
      
      if (itemIndex !== null) {
        const updatedItems = [...menuItems];
        updatedItems[itemIndex] = { ...updatedItems[itemIndex], image_url: imageUrl };
        setMenuItems(updatedItems);
      } else {
        setCustomHeaderImage(imageUrl);
      }
    };
    reader.readAsDataURL(file);
  };

  // Update menu item
  const updateMenuItem = (index, field, value) => {
    const updatedItems = [...menuItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setMenuItems(updatedItems);
  };

  // Add tag to item
  const addTagToItem = (index, tag) => {
    const updatedItems = [...menuItems];
    const currentTags = updatedItems[index].tags || [];
    if (!currentTags.includes(tag)) {
      updatedItems[index] = { ...updatedItems[index], tags: [...currentTags, tag] };
      setMenuItems(updatedItems);
    }
  };

  // Remove tag from item
  const removeTagFromItem = (index, tagToRemove) => {
    const updatedItems = [...menuItems];
    updatedItems[index] = {
      ...updatedItems[index],
      tags: (updatedItems[index].tags || []).filter(t => t !== tagToRemove)
    };
    setMenuItems(updatedItems);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="menu-generator-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="w-5 h-5 text-primary" />
            Générateur de Menu — {foodTruck?.nom}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="template" className="text-xs sm:text-sm" data-testid="tab-template">
              <Palette className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Template</span>
            </TabsTrigger>
            <TabsTrigger value="items" className="text-xs sm:text-sm" data-testid="tab-items">
              <FileText className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Plats</span>
            </TabsTrigger>
            <TabsTrigger value="ai" className="text-xs sm:text-sm" data-testid="tab-ai">
              <Sparkles className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">IA</span>
            </TabsTrigger>
            <TabsTrigger value="preview" className="text-xs sm:text-sm" data-testid="tab-preview">
              <Eye className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Aperçu</span>
            </TabsTrigger>
          </TabsList>

          {/* Template Selection */}
          <TabsContent value="template" className="flex-1 overflow-auto p-4">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-3">Choisissez un template</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {TEMPLATES.map(template => (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      data-testid={`template-${template.id}`}
                      className={`cursor-pointer rounded-xl border-2 p-4 transition-all hover:shadow-lg ${
                        selectedTemplate === template.id 
                          ? 'border-primary ring-2 ring-primary/20 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div 
                        className="h-20 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden"
                        style={{ background: template.preview }}
                      >
                        <span 
                          className="text-lg font-bold tracking-wider"
                          style={{ color: template.accent }}
                        >
                          MENU
                        </span>
                        {selectedTemplate === template.id && (
                          <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                            <Check className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                      <div 
                        className="h-1 rounded-full mb-3"
                        style={{ backgroundColor: template.accent }}
                      />
                      <p className="text-sm font-semibold">{template.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Color Customization */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-medium">Personnaliser les couleurs</h4>
                    <p className="text-sm text-muted-foreground">Modifier les couleurs du template sélectionné</p>
                  </div>
                  <Switch
                    checked={useCustomColors}
                    onCheckedChange={setUseCustomColors}
                    data-testid="toggle-custom-colors"
                  />
                </div>
                
                {useCustomColors && (
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div>
                      <Label className="text-xs">Fond</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={customBackground}
                          onChange={(e) => setCustomBackground(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border"
                        />
                        <Input
                          value={customBackground}
                          onChange={(e) => setCustomBackground(e.target.value)}
                          className="flex-1 h-10 font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Texte</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={customTextColor}
                          onChange={(e) => setCustomTextColor(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border"
                        />
                        <Input
                          value={customTextColor}
                          onChange={(e) => setCustomTextColor(e.target.value)}
                          className="flex-1 h-10 font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Accent</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={customAccentColor}
                          onChange={(e) => setCustomAccentColor(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border"
                        />
                        <Input
                          value={customAccentColor}
                          onChange={(e) => setCustomAccentColor(e.target.value)}
                          className="flex-1 h-10 font-mono text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Header & Tagline */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Image d'en-tête (optionnel)</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={customHeaderImage}
                      onChange={(e) => setCustomHeaderImage(e.target.value)}
                      placeholder="URL de l'image..."
                      className="flex-1"
                      data-testid="input-header-image"
                    />
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e)}
                      />
                      <Button variant="outline" size="icon" asChild>
                        <span><Upload className="w-4 h-4" /></span>
                      </Button>
                    </label>
                  </div>
                  {customHeaderImage && (
                    <img src={customHeaderImage} alt="Header" className="mt-2 h-20 rounded object-cover" />
                  )}
                </div>
                <div>
                  <Label>Slogan / Accroche</Label>
                  <Input
                    value={customTagline}
                    onChange={(e) => setCustomTagline(e.target.value)}
                    placeholder="Ex: Les meilleures crêpes de la région..."
                    className="mt-1"
                    data-testid="input-tagline"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Menu Items */}
          <TabsContent value="items" className="flex-1 overflow-auto p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Plats du menu ({menuItems.length})</h3>
                <Badge variant="outline">{selectedDay}</Badge>
              </div>
              
              {menuItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Coffee className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun plat dans le menu pour {selectedDay}</p>
                  <p className="text-sm">Ajoutez des plats dans l'onglet Menu du food truck</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-auto pr-2">
                  {menuItems.map((item, index) => (
                    <Card key={index} className="p-3" data-testid={`menu-item-${index}`}>
                      <div className="flex gap-3">
                        {/* Image */}
                        <div className="flex-shrink-0">
                          {item.image_url ? (
                            <img 
                              src={item.image_url} 
                              alt={item.nom}
                              className="w-16 h-16 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                              <Pizza className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex gap-1 mt-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="h-6 text-xs px-1"
                              onClick={() => handleGenerateIcon(index)}
                              disabled={iconGenerating[index]}
                              title="Générer avec IA"
                              data-testid={`btn-generate-icon-${index}`}
                            >
                              {iconGenerating[index] ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Wand2 className="w-3 h-3" />
                              )}
                            </Button>
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, index)}
                              />
                              <Button variant="outline" size="sm" className="h-6 text-xs px-1" asChild title="Uploader une image">
                                <span><Upload className="w-3 h-3" /></span>
                              </Button>
                            </label>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Input
                              value={item.nom}
                              onChange={(e) => updateMenuItem(index, 'nom', e.target.value)}
                              className="font-medium h-8"
                            />
                            <Input
                              type="number"
                              value={item.prix}
                              onChange={(e) => updateMenuItem(index, 'prix', parseFloat(e.target.value) || 0)}
                              className="w-20 h-8"
                              step="0.5"
                            />
                            <span className="text-sm">€</span>
                          </div>
                          
                          <Input
                            value={item.description || ""}
                            onChange={(e) => updateMenuItem(index, 'description', e.target.value)}
                            placeholder="Description du plat..."
                            className="h-8 text-sm"
                          />
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            {(item.tags || []).map((tag, tagIdx) => (
                              <Badge 
                                key={tagIdx} 
                                variant="secondary"
                                className="cursor-pointer hover:bg-destructive hover:text-white transition-colors"
                                onClick={() => removeTagFromItem(index, tag)}
                              >
                                {tag} ×
                              </Badge>
                            ))}
                            <Select onValueChange={(tag) => addTagToItem(index, tag)}>
                              <SelectTrigger className="h-6 w-24 text-xs">
                                <Plus className="w-3 h-3" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="HOT">🔥 HOT</SelectItem>
                                <SelectItem value="ICED">❄️ ICED</SelectItem>
                                <SelectItem value="VEGAN">🌱 VEGAN</SelectItem>
                                <SelectItem value="SPICY">🌶️ SPICY</SelectItem>
                                <SelectItem value="NEW">✨ NEW</SelectItem>
                                <SelectItem value="BEST">⭐ BEST</SelectItem>
                                <SelectItem value="MAISON">🏠 MAISON</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* AI Enhancement */}
          <TabsContent value="ai" className="flex-1 overflow-auto p-4">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-4">Options IA</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label>Fournisseur IA pour le texte</Label>
                    <Select value={aiProvider} onValueChange={setAiProvider}>
                      <SelectTrigger className="mt-1" data-testid="select-ai-provider">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="claude">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-600" />
                            Claude (Anthropic) — Sonnet
                          </div>
                        </SelectItem>
                        <SelectItem value="openai">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-600" />
                            GPT-4o (OpenAI)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Les images sont toujours générées avec DALL-E (OpenAI)
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-lg">
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Améliorer les descriptions
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        L'IA suggère des descriptions appétissantes, tags et catégories
                      </p>
                    </div>
                    <Button 
                      onClick={handleEnhanceMenu}
                      disabled={isEnhancing || menuItems.length === 0}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      data-testid="btn-enhance-menu"
                    >
                      {isEnhancing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Améliorer
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-100 rounded-lg">
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        <Image className="w-4 h-4 text-blue-600" />
                        Génération d'icônes (DALL-E)
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Générez des images individuelles dans l'onglet Plats
                      </p>
                    </div>
                    <Badge variant="outline" className="text-blue-600 border-blue-200">
                      Disponible
                    </Badge>
                  </div>
                </div>
              </div>

              {enhancedItems.length > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Menu amélioré ! Les descriptions et tags ont été mis à jour.
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Preview */}
          <TabsContent value="preview" className="flex-1 overflow-auto p-4">
            {generatedHtml ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold">Aperçu du menu (A4 Paysage)</h3>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={handleGenerateMenu} data-testid="btn-regenerate">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Régénérer
                    </Button>
                    <Button size="sm" onClick={handleExportPdf} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-export-pdf">
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    <Button size="sm" onClick={handleExportPdf} data-testid="btn-print">
                      <Printer className="w-4 h-4 mr-2" />
                      Imprimer
                    </Button>
                  </div>
                </div>
                
                <div 
                  ref={previewRef}
                  className="border rounded-lg overflow-auto bg-gray-100 shadow-inner"
                  style={{ height: "500px" }}
                >
                  <iframe
                    srcDoc={generatedHtml}
                    title="Menu Preview"
                    className="w-full h-full bg-white"
                    style={{ minWidth: "900px" }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  💡 Conseil : Utilisez le bouton PDF ou Ctrl+P pour exporter en PDF haute qualité
                </p>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">Aucun aperçu disponible</p>
                <p className="text-sm mt-2">Cliquez sur "Générer le menu" pour voir l'aperçu</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4 flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} data-testid="btn-close">
            Fermer
          </Button>
          <Button 
            onClick={handleGenerateMenu}
            disabled={isGenerating || menuItems.length === 0}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            data-testid="btn-generate-menu"
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Wand2 className="w-4 h-4 mr-2" />
            )}
            Générer le menu
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MenuGeneratorModal;
